import { Link, useNavigate } from "react-router-dom";
import TeamBadge from "@/components/base/TeamBadge";
import { matches } from "@/mocks/matches";
import { formatViewers } from "@/utils/format";
import type { Match } from "@/types";

function LiveCard({ match, featured }: { match: Match; featured?: boolean }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-lg border border-primary-800 ${
        featured ? "lg:col-span-2 lg:row-span-1" : ""
      }`}
    >
      <img
        src={match.cover}
        alt={`${match.home.name} 对阵 ${match.away.name}`}
        title={`${match.home.name} vs ${match.away.name} 直播 - 篮球直播`}
        className={`w-full object-top object-cover group-hover:scale-105 transition-transform duration-700 ${
          featured ? "h-64 md:h-80" : "h-52 md:h-60"
        }`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/10" />
      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-[11px] font-label font-semibold">
        <span className="bb-live-dot w-1.5 h-1.5 rounded-full bg-background-50 dark:bg-foreground-950" />
        直播中 · {match.period}
      </span>
      <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-950/70 text-background-50 text-[11px] font-label">
        <i className="ri-eye-line" />
        {formatViewers(match.viewers)} 人在看
      </span>

      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
        <p className="text-xs text-primary-200 mb-2">
          {match.leagueName} · {match.stage}
        </p>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <TeamBadge shortName={match.home.shortName} color={match.home.color} size="sm" />
            <span className="text-sm text-background-50 font-medium truncate">
              {match.home.shortName}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-background-50/15 border border-background-50/20">
            <span className="font-heading font-semibold text-xl text-accent-400">
              {match.homeScore}
            </span>
            <span className="text-primary-200">:</span>
            <span className="font-heading font-semibold text-xl text-accent-400">
              {match.awayScore}
            </span>
          </div>
          <div className="flex items-center gap-2 min-w-0 justify-end">
            <span className="text-sm text-background-50 font-medium truncate">
              {match.away.shortName}
            </span>
            <TeamBadge shortName={match.away.shortName} color={match.away.color} size="sm" />
          </div>
        </div>
        <Link
          to={`/match/${match.id}`}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-live-line text-lg" />
          进入直播
        </Link>
      </div>
    </div>
  );
}

export default function LiveSpotlight() {
  const navigate = useNavigate();
  const liveMatches = matches.filter((m) => m.status === "live");
  const upcoming = matches.filter((m) => m.status === "upcoming").slice(0, 4);

  return (
    <section id="live" className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <span className="font-label text-xs tracking-[0.3em] text-accent-600 uppercase">
            Live Now
          </span>
          <h2 className="font-heading font-semibold text-2xl md:text-3xl text-foreground-950 mt-1">
            焦点直播
          </h2>
        </div>
        <Link
          to="/schedule"
          className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
        >
          全部赛程
          <i className="ri-arrow-right-s-line" />
        </Link>
      </div>

      {liveMatches.length === 0 ? (
        <div className="bg-background-50 rounded-lg border border-background-200 py-16 text-center">
          <i className="ri-tv-2-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-700">当前暂无直播中的比赛</p>
          <p className="mt-1 text-sm text-foreground-500">
            可先查看下方即将开始的赛程，或前往赛程页按日期筛选
          </p>
          <button
            type="button"
            onClick={() => navigate("/schedule")}
            className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            查看近期赛程
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {liveMatches.map((m, idx) => (
            <LiveCard key={m.id} match={m} featured={idx === 0} />
          ))}
          <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 lg:col-span-1">
            <h3 className="font-heading text-base text-foreground-950 flex items-center gap-2 mb-4">
              <i className="ri-time-line text-accent-600" />
              即将开始
            </h3>
            <div className="space-y-3">
              {upcoming.map((m) => (
                <Link
                  key={m.id}
                  to={`/match/${m.id}`}
                  className="flex items-center gap-3 p-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="font-label text-sm text-accent-600 shrink-0 w-11">
                    {m.startTime.slice(11, 16)}
                  </span>
                  <span className="text-sm text-foreground-800 truncate">
                    {m.home.shortName} vs {m.away.shortName}
                  </span>
                  <span className="ml-auto text-[11px] text-foreground-500 shrink-0">
                    {m.leagueName}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}